import { IAddress, IClientEntity, IPlanningItemEntity } from '@phrospr/lib-models';
import { getNamePlanningItemType, PlanningItemEngine } from './planning-item.engine';

export namespace MailEngine {
    export function getPlanningMailContent(planningItem: IPlanningItemEntity, client?: IClientEntity) {
        const { planning_item_type } = planningItem;
        const executionAddress = planningItem.data?.delivery_address;
        if (planning_item_type === 'maintenance' && client) {
            let content = getPlanningMailClientContent(client, executionAddress);
            if (planningItem.data?.description) {
                content += `
Beschrijving ${getNamePlanningItemType(planning_item_type)}
${planningItem.data.description}`;
            }
            return content;
        }
        if (planningItem.planning_item_type === 'lead' && client) {
            return getPlanningMailLeadContent(client);
        }
        let content = `
    ${getNamePlanningItemType(planningItem.planning_item_type)} nummer: ${planningItem.planning_item_type_number}\n`;
        content += getPlanningMailClientContent(client, executionAddress);
        content += `
Beschrijving ${getNamePlanningItemType(planningItem.planning_item_type)}
${planningItem.data.description}`;
        return content;
    }

    // Mirrors the branching of getPlanningMailContent: lead events with a client
    // get lead info instead of the planning item description, so regenerating
    // their body after a description edit would only clobber manual edits
    // without adding anything. Every other type's template carries the description.
    export function planningMailContentContainsDescription(
        planningItem: IPlanningItemEntity,
        client?: IClientEntity,
    ): boolean {
        if (planningItem.planning_item_type === 'lead' && client) return false;
        return true;
    }

    const normalizeAddressField = (value: string | null | undefined): string => (value ?? '').trim().toLowerCase();

    const sameAddress = (a: IAddress | null | undefined, b: IAddress | null | undefined): boolean =>
        Boolean(a && b) &&
        normalizeAddressField(a.address) === normalizeAddressField(b.address) &&
        normalizeAddressField(a.zip_code) === normalizeAddressField(b.zip_code) &&
        normalizeAddressField(a.city) === normalizeAddressField(b.city);

    // executionAddress = the job-site address of THIS visit (planning item), not the client's
    // first delivery address; the block is omitted when it is just the main address printed
    // above. The client's other delivery addresses (not main, not this visit) follow it.
    // Keep in lockstep with the portal port in frontend-react-minimal utils/calendar-item.ts.
    export function getPlanningMailClientContent(client: IClientEntity, executionAddress?: IAddress): string {
        const { client_data } = client;
        const { main_address, company_data } = client_data;
        const executionBlock =
            executionAddress && !sameAddress(executionAddress, main_address)
                ? `

Uitvoerings adres
${executionAddress.address ?? ''}
${executionAddress.zip_code ?? ''} ${executionAddress.city ?? ''}

`
                : '';
        const otherAddresses = (client_data.delivery_addresses ?? []).filter(
            address =>
                !address.deleted && !sameAddress(address, main_address) && !sameAddress(address, executionAddress),
        );
        const otherAddressesBlock = otherAddresses.length
            ? `

Andere uitvoerings adressen
${otherAddresses.map(address => `${address.address ?? ''}, ${address.zip_code ?? ''} ${address.city ?? ''}`).join('\n')}

`
            : '';
        return `Klant nummer: ${client.exact_client_number ? client.exact_client_number : ''}
Klant naam: ${PlanningItemEngine.getClientDataName(client_data)}
Klant email: ${client_data.email}
Klant telefoon nr: ${client_data.telephone_phone ? client_data.telephone_phone : '/'}
Klant gsm nr: ${client_data.mobile_phone ? client_data.mobile_phone : '/'}

Klant BTW: ${company_data && company_data.vat_number ? company_data.vat_number : '/'}

Hoofd adres
${main_address.address}
${main_address.city} ${main_address.zip_code}
${
    main_address.stateName
        ? `
${main_address.stateName}`
        : ''
}
${main_address.country} ${main_address.countryName}
${executionBlock}${otherAddressesBlock}${
            client_data.remarks
                ? `

Klant opmerkingen uit Exact
${client_data.remarks}
`
                : ''
        }`;
    }

    export function getPlanningMailLeadContent(lead: IClientEntity): string {
        return `Client nummer Aquagroup: ${lead.client_id}
Lead nummer ${lead.lead_type?.name ?? ''}: ${lead.lead_data.lead_custom_id}
Lead naam: ${PlanningItemEngine.getClientName(lead)}
Lead email: ${lead.client_data.email}${
            lead.client_data.company_data && lead.client_data.company_data.company_name
                ? `
Lead bedrijf naam: ${lead.client_data.company_data.company_name}`
                : ''
        }${
            lead.client_data.company_data && lead.client_data.company_data.vat_number
                ? `
Lead vat nummer: ${lead.client_data.company_data.vat_number}`
                : ''
        }${
            lead.client_data.mobile_phone
                ? `
Lead mobiel nummer: ${lead.client_data.mobile_phone}`
                : ''
        }${
            lead.client_data.telephone_phone
                ? `
Lead telefoon nummer: ${lead.client_data.telephone_phone}`
                : ''
        }

Hoofd adres
${lead.client_data.main_address.address}
${lead.client_data.main_address.city} ${lead.client_data.main_address.zip_code}
${
    lead.client_data.main_address.country
        ? `
Land code: ${lead.client_data.main_address.country}`
        : ''
}

Lead omschrijving: ${lead.lead_data.description}${
            lead.lead_data.detail
                ? `
Lead detail info: ${lead.lead_data.detail}`
                : ''
        }
`;
    }
}

export function isValidEmail(email: string): boolean {
    if (!email) return false;
    const sanitizedEmail = email.trim();
    const emailRegexp =
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return emailRegexp.test(sanitizedEmail);
}
